// /reset-password?token=... — user lands here from email, sets new password
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Loader2, Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => { if (!token) setMissing(true); }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.new_password !== form.confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: form.new_password });
      setDone(true);
      toast.success("Password reset — please sign in with your new password");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/" data-testid="reset-home-link"><Logo size={28} /></Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white border border-zinc-200 rounded p-8 shadow-sm" data-testid="reset-card">
            {missing ? (
              <div className="text-center" data-testid="reset-missing-token">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <h1 className="text-xl font-bold text-zinc-900 mb-2">Missing reset token</h1>
                <p className="text-sm text-zinc-500 mb-4">
                  This page is meant to be opened from a password reset email. If you got here by mistake, request a new link below.
                </p>
                <Link to="/forgot-password" className="inline-block text-sm text-[#002FA7] hover:underline font-medium">Request reset link</Link>
              </div>
            ) : done ? (
              <div className="text-center" data-testid="reset-success-state">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h1 className="text-xl font-bold text-zinc-900 mb-2">Password updated</h1>
                <p className="text-sm text-zinc-500 mb-4">You can now sign in with your new password.</p>
                <button
                  onClick={() => navigate("/login")}
                  className="px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium rounded"
                  data-testid="reset-success-signin-btn"
                >
                  Go to sign in
                </button>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mb-1">Set a new password</h1>
                <p className="text-sm text-zinc-500 mb-6">Choose something you can remember.</p>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Lock className="w-3 h-3" /> New password</label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      autoFocus
                      value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      placeholder="At least 8 characters"
                      className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded text-sm bg-white"
                      data-testid="reset-new-password-input"
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Confirm new password</label>
                    <input
                      type="password"
                      required
                      value={form.confirm}
                      onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded text-sm bg-white"
                      data-testid="reset-confirm-input"
                      autoComplete="new-password"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2.5 px-4 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium rounded inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="reset-submit-btn"
                  >
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : "Update password"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
